import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { FileText, CheckCircle, Download, AlertCircle, Loader2, ShoppingCart } from 'lucide-react';
import { contractsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';

const CheckoutContract = () => {
  const navigate = useNavigate();
  const { addItem, items: cartItems } = useCart();
  const [bookingData, setBookingData] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const [contractId, setContractId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const data = sessionStorage.getItem('bookingData');
    if (!data) {
      navigate('/');
      return;
    }
    const parsed = JSON.parse(data);
    setBookingData(parsed);
    loadContract(parsed);
  }, [navigate]);

  const loadContract = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const personalData = data.personalData || {};
      
      // Formatar data de nascimento
      const birthDate = personalData.birthDay && personalData.birthMonth && personalData.birthYear
        ? `${personalData.birthDay}/${personalData.birthMonth}/${personalData.birthYear}`
        : 'Não informado';

      // Gerar contrato baseado na categoria do pacote
      const contractData = {
        reservation_id: `temp_${Date.now()}`, // ID temporário até criar reserva
        customer_name: personalData.fullName || 'Não informado',
        customer_cpf: personalData.cpf || 'Não informado',
        customer_birth_date: birthDate,
        package_name: data.packageName || 'Não informado',
        package_category: data.packageCategory || 'nacional-aereo',
        package_id: data.packageId || '', // ID do pacote para buscar países
        travel_date: data.selectedDate || 'Não informado',
        total_value: formatPrice(data.totalPrice || 0)
      };

      const response = await contractsAPI.generate(contractData);
      setContractContent(response.content);
      setContractId(response.id);
    } catch (err) {
      console.error('Erro ao carregar contrato:', err);
      setError('Não foi possível carregar o contrato. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const getCategoryName = (category) => {
    const names = {
      'nacional-aereo': 'Nacional Aéreo',
      'internacional-aereo': 'Internacional',
      'passeio-1-dia': 'Passeio de 1 Dia',
      'rodoviario': 'Rodoviário'
    };
    return names[category] || category;
  };

  const handleAcceptAndContinue = async () => {
    if (!agreed) {
      alert('Você precisa aceitar os termos do contrato para continuar.');
      return;
    }

    try {
      // Marcar contrato como aceito
      if (contractId) {
        await contractsAPI.accept(contractId);
      }

      const updatedData = {
        ...bookingData,
        contractId,
        contractAccepted: true,
        contractAcceptedAt: new Date().toISOString(),
        contractContent
      };

      // Se já houver itens no carrinho, agrega o pacote atual e segue para pagamento agrupado
      if (cartItems && cartItems.length > 0) {
        addItem({
          packageId: updatedData.packageId,
          packageName: updatedData.packageName,
          packageCategory: updatedData.packageCategory,
          packageImage: updatedData.packageImage,
          packagePrice: updatedData.packagePrice,
          dateId: updatedData.dateId,
          dateLabel: updatedData.selectedDate,
          travelersCount: (updatedData.allTravelers && updatedData.allTravelers.length) || 1,
          totalPrice: updatedData.totalPrice,
          contractId,
          contractAccepted: true,
          contractAcceptedAt: new Date().toISOString(),
          contractContent,
          personalData: updatedData.personalData,
          allTravelers: updatedData.allTravelers,
          boardingData: updatedData.boardingData,
          bookingData: { ...updatedData }
        });
        sessionStorage.removeItem('bookingData');
        // CheckoutStep4 lerá cartCheckout do storage
        const allItems = [...cartItems, {
          id: `${updatedData.packageId}_${updatedData.dateId}_${Date.now()}`,
          packageId: updatedData.packageId,
          packageName: updatedData.packageName,
          packageCategory: updatedData.packageCategory,
          packageImage: updatedData.packageImage,
          dateId: updatedData.dateId,
          dateLabel: updatedData.selectedDate,
          travelersCount: (updatedData.allTravelers && updatedData.allTravelers.length) || 1,
          totalPrice: updatedData.totalPrice,
          contractAccepted: true,
          allTravelers: updatedData.allTravelers,
          personalData: updatedData.personalData,
          bookingData: { ...updatedData }
        }];
        const cartTotal = allItems.reduce((s, it) => s + (Number(it.totalPrice) || 0), 0);
        sessionStorage.setItem('cartCheckout', JSON.stringify({ items: allItems, total: cartTotal, count: allItems.length }));
        navigate('/checkout/pagamento');
        return;
      }

      // Sem carrinho — fluxo single-package
      sessionStorage.setItem('bookingData', JSON.stringify(updatedData));
      navigate('/checkout/pagamento');
    } catch (err) {
      console.error('Erro ao aceitar contrato:', err);
      const updatedData = {
        ...bookingData,
        contractId,
        contractAccepted: true,
        contractAcceptedAt: new Date().toISOString(),
        contractContent
      };
      sessionStorage.setItem('bookingData', JSON.stringify(updatedData));
      navigate('/checkout/pagamento');
    }
  };

  const handleAddToCartAndContinue = async () => {
    if (!agreed) {
      alert('Você precisa aceitar os termos do contrato para continuar.');
      return;
    }
    try {
      if (contractId) {
        await contractsAPI.accept(contractId);
      }
    } catch (e) {
      console.warn('Falha ao marcar contrato como aceito (seguindo mesmo assim):', e);
    }

    // Adiciona ao carrinho com todos os dados do contrato assinado
    addItem({
      packageId: bookingData.packageId,
      packageName: bookingData.packageName,
      packageCategory: bookingData.packageCategory,
      packageImage: bookingData.packageImage,
      packagePrice: bookingData.packagePrice,
      dateId: bookingData.dateId,
      dateLabel: bookingData.selectedDate,
      travelersCount: (bookingData.allTravelers && bookingData.allTravelers.length) || 1,
      totalPrice: bookingData.totalPrice,
      contractId,
      contractAccepted: true,
      contractAcceptedAt: new Date().toISOString(),
      contractContent,
      personalData: bookingData.personalData,
      allTravelers: bookingData.allTravelers,
      boardingData: bookingData.boardingData,
      // dados originais completos para reconstrução da reserva no checkout final
      bookingData: { ...bookingData, contractId, contractAccepted: true, contractContent }
    });

    // Limpa session para iniciar nova compra
    sessionStorage.removeItem('bookingData');
    navigate('/pacotes');
  };

  const handleDownloadContract = () => {
    // Criar HTML para download
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Contrato - TÔ INDO ALI</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { text-align: center; color: #003366; }
    h2 { text-align: center; color: #666; font-size: 1.1rem; }
    h3 { color: #003366; margin-top: 20px; }
    .customer-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 15px 0; }
    ul { margin-left: 20px; }
    .signature-date { text-align: right; margin-top: 30px; font-style: italic; }
  </style>
</head>
<body>
  ${contractContent}
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Contrato_ToIndoAli_${bookingData?.packageName?.replace(/\s+/g, '_') || 'Viagem'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Progress */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 min-w-max px-4">
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Seleção</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Dados</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Embarque</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-[#FFD700]"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#FFD700] flex items-center justify-center font-bold text-gray-900 text-xs md:text-sm">4</div>
              <span className="ml-1 md:ml-2 font-medium text-gray-900 text-xs md:text-sm">Contrato</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">5</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Pagamento</span>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-4 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#FFD700]" />
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Contrato de Serviços</h2>
                  <p className="text-sm text-gray-500">{getCategoryName(bookingData.packageCategory)}</p>
                </div>
              </div>
              {contractContent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadContract}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden md:inline">Baixar</span>
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
                <span className="ml-3 text-gray-600">Carregando contrato...</span>
              </div>
            ) : error ? (
              <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-700 mb-4">{error}</p>
                <Button onClick={() => loadContract(bookingData)} variant="outline">
                  Tentar Novamente
                </Button>
              </div>
            ) : (
              <>
                {/* Resumo do Cliente */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">📋 Dados do Contratante</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-blue-700">
                    <p><strong>Nome:</strong> {bookingData.personalData?.fullName}</p>
                    <p><strong>CPF:</strong> {bookingData.personalData?.cpf}</p>
                    <p><strong>Nascimento:</strong> {bookingData.personalData?.birthDay}/{bookingData.personalData?.birthMonth}/{bookingData.personalData?.birthYear}</p>
                  </div>
                </div>

                {/* Contrato */}
                <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-600">Leia atentamente o contrato abaixo:</p>
                  </div>
                  <div 
                    className="p-4 md:p-6 max-h-[400px] overflow-y-auto bg-white contract-content"
                    dangerouslySetInnerHTML={{ __html: contractContent }}
                  />
                </div>

                {/* Aceite */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="agree" 
                      checked={agreed} 
                      onCheckedChange={setAgreed} 
                      className="mt-1 data-[state=checked]:bg-[#003366]" 
                    />
                    <Label htmlFor="agree" className="cursor-pointer text-gray-700 leading-relaxed text-sm md:text-base">
                      <span className="font-semibold">Li e aceito</span> todos os termos e condições estabelecidos neste contrato. 
                      Estou ciente das políticas de cancelamento e das minhas obrigações como contratante.
                    </Label>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => navigate(-1)} 
                      variant="outline" 
                      className="flex-1 order-2 sm:order-1 min-h-[48px]"
                      data-testid="contract-back-btn"
                    >
                      Voltar
                    </Button>
                    <Button
                      onClick={handleAcceptAndContinue}
                      disabled={!agreed}
                      className="flex-1 bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold order-1 sm:order-2 min-h-[56px] py-4 whitespace-normal text-sm md:text-base"
                      data-testid="contract-accept-pay-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span>Aceitar e Continuar para Pagamento</span>
                    </Button>
                  </div>
                  <Button
                    onClick={handleAddToCartAndContinue}
                    disabled={!agreed}
                    variant="outline"
                    className="w-full min-h-[52px] py-3 whitespace-normal text-sm md:text-base border-2 border-theme-primary text-theme-primary hover:bg-theme-light disabled:opacity-50"
                    data-testid="contract-add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>Adicionar ao Carrinho e Continuar Comprando</span>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <style>{`
          .contract-content h1 {
            font-size: 1.25rem;
            font-weight: bold;
            text-align: center;
            color: #003366;
            margin-bottom: 0.5rem;
          }
          .contract-content h2 {
            font-size: 1rem;
            text-align: center;
            color: #666;
            margin-bottom: 1.5rem;
          }
          .contract-content h3 {
            font-size: 0.95rem;
            font-weight: bold;
            margin-top: 1.5rem;
            margin-bottom: 0.5rem;
            color: #003366;
          }
          .contract-content .customer-info {
            background: #f5f5f5;
            padding: 1rem;
            border-radius: 0.5rem;
            margin: 1rem 0;
          }
          .contract-content ul {
            margin-left: 1.5rem;
            list-style-type: disc;
          }
          .contract-content li {
            margin-bottom: 0.25rem;
          }
          .contract-content p {
            margin-bottom: 0.5rem;
          }
          .contract-content .signature-date {
            margin-top: 2rem;
            text-align: right;
            font-style: italic;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CheckoutContract;
